"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Game = void 0;
const bitiotic_1 = require("@bitiotic/bitiotic");
const GameOptions_1 = require("./GameOptions");
const Round_1 = require("./Round");
const Persistent = require("./Persistent");
const Lobby_1 = require("./Lobby");
const ParamValidation_1 = require("./ParamValidation");
/*
 * TODO
 *
 * - Add a History log, and details on objects.
 * - Track "device" that made a Player's update.
 * - Check Round is in correct status for guess/bet/close/etc
 * - Check the Path strings against expected lists.
 * - Tag "Untrusted<>" string coming from end user?
 *
 * - Add listeners for Game: managers, players, new Round
 * - Track if current user is manager or not (Round too?)
 */
class Game {
    // Make sure local writes can't resolve out of order.
    enqueueWrite(prom) {
        const chain = this.liveWrite.then(() => {
            return prom;
        });
        this.liveWrite = chain;
        return chain;
    }
    static gameCollection(ctx, db) {
        return db.makeCollectionRef(ctx, 'games');
    }
    static gameDoc(ctx, db, gamePath) {
        const coll = Game.gameCollection(ctx, db);
        if (gamePath) {
            return coll.makeDocRef(gamePath);
        }
        else {
            return coll.newDocRef();
        }
    }
    static roundsCollection(ctx, gameDoc) {
        return gameDoc
            .makeCollectionRef(ctx, 'rounds');
    }
    static requestToJoinCollection(ctx, gameDoc) {
        return gameDoc
            .makeCollectionRef(ctx, 'requestToJoin');
    }
    static requestToJoinDocRef(ctx, gameDoc, playerPath) {
        return this.requestToJoinCollection(ctx, gameDoc)
            .makeDocRef(playerPath);
    }
    /**
     * Figure out what the given player's relationship to the given Game is.  Game may not
     * exist or may be complete.
     */
    static getGameJoinState(ctx, db, gamePath, currentPlayerPath) {
        // TODO: This should all be expressed as a Firestore query.
        // Game is not visible unless player has joined.
        const gameDoc = Game.gameDoc(ctx, db, gamePath);
        const req = Game.requestToJoinDocRef(ctx, gameDoc, currentPlayerPath);
        ctx.log('get', req.id());
        return req.get().then(reqRef => {
            // No request-to-join even exists ...
            ctx.log('exists?', req.id());
            if (!reqRef.docExists()) {
                ctx.log('get game', gameDoc.id());
                return gameDoc.get().then(gameRef => {
                    ctx.log('game exists?', gameDoc.id());
                    if (!gameRef.docExists()) {
                        // If the game doesn't even exist, there is no point in adding join
                        return 'nogame';
                    }
                    const gameData = gameRef.data();
                    if (!gameData) {
                        ctx.log('Corrupt game, no data:', gameDoc.id());
                        return 'nogame';
                    }
                    if (gameData.managers.includes(currentPlayerPath)) {
                        ctx.log('Am manager of game', gameDoc.id());
                        return 'manager';
                    }
                    // Need to request to join
                    return 'nojoinreq';
                });
            }
            ctx.log('req is valid?', req.id());
            const reqToJoin = reqRef.data();
            if (!reqToJoin || !reqToJoin.ready) {
                ctx.log('ReqToJoin corrupted?', gamePath, currentPlayerPath, reqToJoin);
                return 'nojoinreq';
            }
            ctx.log('req is acked?', req.id());
            if (!reqToJoin.accepted || !reqToJoin.accepted[currentPlayerPath]) {
                // Outstanding request to join
                return 'nojoinack';
            }
            ctx.log('game is done?', req.id());
            return gameDoc.get().then(gameRef => {
                if (!gameRef.docExists()) {
                    ctx.log('Stale ReqToJoin?  No game for joined Req:', gamePath, currentPlayerPath, reqToJoin);
                    return 'nogame';
                }
                const game = gameRef.data();
                if (!game) {
                    ctx.log('Stale ReqToJoin?  No game body for joined Req:', gamePath, currentPlayerPath, reqToJoin);
                    return 'nogame';
                }
                if (game.completed) {
                    return 'completed';
                }
                ctx.log('req is acked, game is active');
                return 'joined';
            });
        });
    }
    static createRequestToJoin(ctx, db, gamePath, driverPlayerPath, playerPath) {
        const update = {
            ready: true,
        };
        if (driverPlayerPath !== playerPath) {
            update.secondaries = {
                [playerPath]: true,
            };
        }
        const gameDoc = Game.gameDoc(ctx, db, gamePath);
        const joinRef = Game.requestToJoinDocRef(ctx, gameDoc, driverPlayerPath);
        return joinRef.set(ctx, update);
    }
    static listenForJoinAccept(ctx, db, fbFuncs, gamePath, driverPlayerPath, playerPath, cb, errCb) {
        // XXX: request-to-join for secondary players on a device does not need
        // to create the Game object.  Just wait for the requestToJoin to be ack'd.
        const gameDoc = Game.gameDoc(ctx, db, gamePath);
        const unsub = bitiotic_1.CancelTracker.track(`Game.${gameDoc.id()}.RequestToJoin.isAccepted`, Game.requestToJoinDocRef(ctx, gameDoc, driverPlayerPath)
            .onSnapshot(reqDoc => {
            if (reqDoc.docExists()) {
                const blob = reqDoc.data();
                if (blob && blob.accepted && blob.accepted[playerPath]) {
                    unsub();
                    // secondary players may not be able to see
                    // the Game (if the primary hasn't been accepted yet).
                    if (driverPlayerPath !== playerPath) {
                        cb(null);
                    }
                    else {
                        Game.loadExisting(ctx, db, fbFuncs, Persistent.asGamePath(gameDoc.id()))
                            .then(g => {
                            cb(g);
                        })
                            .catch(err => {
                            errCb(ctx.chainError(err, 'loading newly found game', gamePath));
                        });
                    }
                }
                else {
                    ctx.log('RequestToJoin exists, but not accepted:', blob);
                }
            }
            else {
                ctx.log('Broken RequestToJoin?', reqDoc.docExists());
            }
        }, (err) => {
            errCb(ctx.chainError(err, 'listenForGame', gamePath, 'driver', driverPlayerPath, 'for', playerPath));
        }));
        return unsub;
    }
    static startNew(ctx, mgrPath, gameOptions, db, fbFuncs, gamePath) {
        const serverStartNewGame = fbFuncs.findCallable('startNewGame');
        const g = new Game(ctx, gameOptions, db, fbFuncs, gamePath);
        g.completedCache = false;
        const args = {
            gamePath: g.getGamePath(),
            options: g.gameOptions.toPersist(ctx),
            manager: mgrPath, // Implicit with caller auth?
        };
        return serverStartNewGame(args)
            .catch(err => {
            throw ctx.chainError(err, 'starting new game');
        }).then(result => {
            ctx.log('Created game', g.getGamePath(), 'got', result);
            ctx.assertEqual(result.status, 'ok');
            return g;
        });
    }
    static validateGameOptions(ctx, oObj) {
        (0, ParamValidation_1.isObjectWithProperties)(ctx, oObj, [
            'managerIsPlayer', 'maxPlayerCount', 'betSpotCount', 'betUnit', 'betGrantCount',
            'betsPerRound', 'bestAnswerGrantCount', 'roundsPerGame', 'collapseDuplicateAnswers',
        ]);
        (0, ParamValidation_1.isBoolProp)(ctx, oObj, 'managerIsPlayer');
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'maxPlayerCount', 2, 50);
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'betSpotCount', 3, 10); // must be odd too, not forced here
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'betUnit', 1, 10 * 1000 * 1000);
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'betGrantCount', 0, 200);
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'betsPerRound', 1, 10);
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'bestAnswerGrantCount', 0, 100);
        (0, ParamValidation_1.isNumProp)(ctx, oObj, 'roundsPerGame', 1, 250);
        (0, ParamValidation_1.isBoolProp)(ctx, oObj, 'collapseDuplicateAnswers');
        return oObj;
    }
    // Callback invoked on the Server-side by the Cloud Function
    static async serverStartNew(ctx, db, auth, fbFuncs, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        ctx.assertDefined(fbFuncs, 'Expect fbFuncs to be defined');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath', 'manager', 'options']);
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        (0, ParamValidation_1.isID)(ctx, untrusted_args.manager);
        (0, ParamValidation_1.isCurrentUser)(ctx, auth, untrusted_args.manager);
        const mgrPath = untrusted_args.manager;
        const gameOptions = Game.validateGameOptions(ctx.newCtx('validateOptions'), untrusted_args.options);
        ctx.log('Server creating game', gamePath, 'with', gameOptions);
        const gOpts = new GameOptions_1.GameOptions(ctx, gameOptions);
        const g = new Game(ctx, gOpts, db, fbFuncs, gamePath);
        const state = {
            options: g.gameOptions.toPersist(ctx),
            managers: [mgrPath],
            players: {}, // manager added as a player later
            completed: false,
            completedRounds: {},
            // rounds is a sub-collection
            // requestToJoin is a sub-collection
        };
        ctx.log('Creating game for', mgrPath, ':', state);
        const mgrDoc = Lobby_1.Lobby.getPlayerDocRef(ctx, db, mgrPath);
        const mgrUpdate = {
            currentGame: gamePath,
        };
        const batch = bitiotic_1.FirebaseV9.writeBatch(ctx, db);
        batch.setDoc(ctx, g.gameDoc, state);
        batch.patchIn(ctx, mgrDoc, mgrUpdate);
        ctx.log('Game', g.gameDoc.id(), 'gets', state);
        ctx.log('Mgr update', mgrDoc.id(), 'is', mgrUpdate, '(a merge)');
        return batch.commit(ctx)
            .then(() => {
            ctx.log('Created new game', g.gameDoc.id());
            // XXX add-mgr should be baked into original batch?
            const isActive = gOpts.managerIsPlayer(ctx);
            return g.internalAddPlayer(ctx, mgrPath, isActive)
                .then(() => {
                ctx.log(`Added manager as player (isActive: ${isActive})`);
                return true;
            })
                .catch(err => {
                throw ctx.chainError(err, 'add manager', mgrPath);
            });
        })
            .catch(err => {
            throw ctx.chainError(err, 'committing manager/player batch');
        });
    }
    updateGameDocCache(state) {
        this.completedCache = state.completed;
        const newPlayerList = Object.keys(state.players);
        this.playerListCache = newPlayerList;
        this.managerListCache = [...state.managers];
    }
    static loadExisting(ctx, db, fbFuncs, path) {
        const gameDoc = Game.gameDoc(ctx, db, path);
        return gameDoc.get().then(docRef => {
            const state = docRef.data();
            ctx.assertDefined(state, 'Game', path, 'has no state.');
            const opts = GameOptions_1.GameOptions.loadGameOptions(ctx, state);
            const g = new Game(ctx, opts, db, fbFuncs, path);
            // Rebuild the cached state from the Game document
            if (state) {
                g.updateGameDocCache(state);
            }
            ctx.log('Found gameDoc for', path, 'fetching rounds ...');
            // Fetch all the existing rounds, and build the roundsCache
            // XXX querySnap should not be "paged"?
            return g.roundsColl.getDocs().then(querySnap => {
                ctx.log('Got', querySnap.size(), 'rounds on game', path);
                querySnap.forEach(doc => {
                    const state = doc.data();
                    ctx.assertDefined(state, 'Round', doc.id(), 'has no state.  In', g.getGamePath());
                    const r = new Round_1.Round(ctx.newCtx('loadRound'), opts, doc.ref(), state);
                    const rpath = r.getRoundPath(ctx);
                    ctx.assertUndefined(g.roundsCache[rpath], 'Reloading already-seen round at', rpath);
                    g.roundsCache[rpath] = r;
                });
            }).then(() => {
                ctx.log('Finished loading rounds for game', path);
                return g;
            });
        }).catch(err => {
            ctx.log('Game load at', path, 'failed:', err);
            throw ctx.chainError(err, 'loadExisting');
        });
    }
    constructor(ctx, gameOptions, db, fbFuncs, path) {
        /**
         * Cache of the Round sub-collection.  Updated when loading an existing game from Firebase, or
         * when listenForNewRound finds out about new Rounds
         */
        this.roundsCache = {};
        this.playerListCache = [];
        this.managerListCache = [];
        // Queue up addPlayer calls to avoid overlapping transactions
        // TODO: this is stale now, as addPlayer is a Cloud Function
        this.addQueue = Promise.resolve();
        this.liveWrite = Promise.resolve();
        ctx.assertDefined(fbFuncs, 'Expect fbFuncs to be defined');
        this.gameOptions = gameOptions.clone(ctx);
        this.gameDoc = Game.gameDoc(ctx, db, path);
        this.requestToJoinColl = Game.requestToJoinCollection(ctx, this.gameDoc);
        this.roundsColl = Game.roundsCollection(ctx, this.gameDoc);
        this.fbFuncs = fbFuncs;
    }
    getOptions() {
        return this.gameOptions;
    }
    getGamePath() {
        return Persistent.asGamePath(this.gameDoc.id());
    }
    isCompleted() {
        return Boolean(this.completedCache);
    }
    pullIsCompleted(ctx) {
        return this.withGameDoc(ctx, g => {
            return Promise.resolve(g.completed);
        });
    }
    // Read-only refresh of game doc from server.  Queued after any currently pending writes.
    withGameDoc(ctx, cb) {
        return this.liveWrite.then(() => {
            return this.gameDoc.get().then(docRef => {
                ctx.assertNotNull(docRef);
                ctx.assertDefined(docRef);
                ctx.assertOk(docRef.docExists(), 'game doc must exist', this.gameDoc.id());
                // ctx.assertOk(!docRef.hasPendingContent(),
                //   'game must have no pending content', this.gameDoc.id());
                if (docRef.hasPendingContent()) {
                    ctx.log('WARNING: has pending writes: GameDoc:', docRef.id(), docRef.ref());
                }
                const blob = docRef.data();
                ctx.assertDefined(blob);
                this.updateGameDocCache(blob); // side-effect: update all the caches
                return cb(blob);
            });
        });
    }
    // XXX remove a player?  (Mark the player as excluded from future rounds?)
    /**
     * Add given player to the game.  Player will be included in any future rounds.
     * Add is idempotent (other than resetting game winnings).
     */
    addPlayer(ctx, playerPath, isActive = true) {
        // Security rules are insane if clients can write to Game, so use a Cloud Function
        // to have the manager add a new player to the game
        const serverAddPlayer = this.fbFuncs.findCallable('addPlayerToGame');
        const addPlayerArgs = {
            gamePath: this.getGamePath(),
            playerPath,
            isActive,
        };
        return serverAddPlayer(addPlayerArgs)
            .catch(err => {
            throw ctx.chainError(err, `adding player ${playerPath} to ${this.getGamePath()}`);
        }).then(result => {
            ctx.log('Added player', playerPath, 'to game', this.getGamePath(), 'result', result);
        });
    }
    /**
     * Actually add given player to game.  Current player should be the manager.  Only invoked on
     * the admin side (via Cloud Function).
     *
     * 'reqToJoin' can be unset when adding a player directly (e.g., when creating new game and
     * adding self, or in some test cases).
     */
    internalAddPlayer(ctx, playerPath, isActive = true, reqToJoin) {
        // Security rules are insane if clients can write to Game, so must come in through a Cloud Function
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        const db = this.gameDoc.getFirestore();
        let driver = null;
        if (reqToJoin) {
            const isSecondary = reqToJoin.id() !== playerPath;
            if (isSecondary) {
                driver = Persistent.asPlayerPath(reqToJoin.id());
            }
        }
        const playerInit = {
            winnings: 0,
            bestGuessCount: 0,
            exactGuessCount: 0,
            winningBetCount: 0,
            isActive,
            driver,
            playerNum: -1, // playerNum filled in below
        };
        const gameDocUpdate = {
            players: {
                [playerPath]: playerInit
            }
        };
        const reqToJoinUpdate = {
            accepted: {
                [playerPath]: true,
            }
        };
        // To avoid spamming the Firebase transactions with overlapping (locally issued) updates,
        // create a Promise-queue of addPlayer updates.  Once the previous update is done, run this
        // update. If more updates come in while this one is running, they will be chained and
        // "addQueue" will point to the most recent update.  When an update is done, if it is the
        // current "addQueue" element, that means all the prior adds are completed, and the queue
        // is full of 'done' promises. So reset to a resolved placeholder Promise.
        //
        // TODO: This serialization thing no longer applies with Cloud Functions ...
        //
        // TODO: synchronize this with the "this.liveWrite" queue
        const priorAdd = this.addQueue;
        let txCt = 0;
        const addTxProm = priorAdd.then(() => {
            return bitiotic_1.FirebaseV9.transaction(ctx, db, tx => {
                txCt++;
                return tx.getDoc(ctx, this.gameDoc).then(gameDoc => {
                    var _a;
                    const maxPlayerCt = this.gameOptions.maximumNumberOfPlayers(ctx);
                    ctx.assertDefined(gameDoc);
                    ctx.assertTrue(gameDoc.docExists(), 'Game doc does not exist', this.gameDoc);
                    const state = gameDoc.data();
                    ctx.assertDefined(state, 'Game doc', this.gameDoc.id(), 'must have data');
                    const currPlayerCt = Object.keys(state.players).length;
                    let isNewPlayer = true; // make a re-add idempotent
                    if (state.players[playerPath] !== undefined) {
                        ctx.log('Warning:', playerPath, 'is already in player list.');
                        isNewPlayer = false;
                    }
                    else if (currPlayerCt >= maxPlayerCt) {
                        ctx.log('Adding player', playerPath);
                        ctx.log('Current players:', Object.keys(state.players));
                        // ctx.rejected(...)
                        return Promise.reject(ctx.newError(`too many players (have ${currPlayerCt}, max ${maxPlayerCt}) add ${playerPath}`));
                    }
                    else {
                        // adding new player, give player a new id
                        const gamePlayerDoc = (_a = gameDocUpdate.players) === null || _a === void 0 ? void 0 : _a[playerPath];
                        ctx.assertDefined(gamePlayerDoc, 'doc for', playerPath);
                        gamePlayerDoc.playerNum = (currPlayerCt + 1); // 1-based, not 0-based
                        tx.patchIn(ctx, this.gameDoc, gameDocUpdate);
                    }
                    if (reqToJoin && isNewPlayer) {
                        tx.patchIn(ctx, reqToJoin, reqToJoinUpdate);
                    }
                    return Promise.resolve();
                });
            }).then(() => {
                ctx.log('addPlayer added', playerPath, 'with tx', txCt);
                this.playerListCache.push(playerPath);
            });
        }).then(() => {
            if (this.addQueue === addTxProm) {
                this.addQueue = Promise.resolve();
            }
        });
        this.addQueue = addTxProm;
        return addTxProm;
    }
    ensureCurrentUserIsManager(ctx, auth) {
        const currUser = auth.currentUser();
        if (currUser === null)
            throw ctx.newError('Invalid current user');
        const currPlayer = Lobby_1.Lobby.pathForUser(currUser);
        if (!this.isManager(ctx, currPlayer)) {
            throw ctx.newError('Current user', currPlayer, 'is not a manager for game', this.getGamePath());
        }
    }
    // Cloud Function callback for clients to request a player to be added to a Game
    static serverAddPlayer(ctx, db, fbFuncs, auth, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath', 'playerPath', 'isActive']);
        // verify the args
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        (0, ParamValidation_1.isID)(ctx, untrusted_args.playerPath);
        const playerPath = untrusted_args.playerPath;
        (0, ParamValidation_1.isBoolProp)(ctx, untrusted_args, 'isActive');
        const isActive = untrusted_args.isActive;
        return Game.loadExisting(ctx, db, fbFuncs, gamePath).then(game => {
            // -- current player must be a manager on the Game
            game.ensureCurrentUserIsManager(ctx, auth);
            // This is only the correct request-to-join doc for "primary" players.  For a "secondary" player
            // we'd have to either do a query, or expect the client to pass along the relevant docID.
            const reqToJoin = Game.requestToJoinDocRef(ctx, game.gameDoc, playerPath);
            // invoke internal addplayer
            return game.internalAddPlayer(ctx, playerPath, isActive, reqToJoin)
                .then(() => {
                return true;
            });
        });
    }
    newRound(ctx, roundNumber) {
        // Security rules are insane if clients can write to Game, so use a Cloud Function
        // to have the manager add a new player to the game
        const serverNewGameRound = this.fbFuncs.findCallable('newGameRound');
        const args = {
            gamePath: this.getGamePath(),
            roundNumber,
        };
        return serverNewGameRound(args)
            .catch(err => {
            throw ctx.chainError(err, `adding round ${roundNumber} to game ${this.getGamePath()}`);
        }).then(result => {
            ctx.log('Added round', roundNumber, 'to game', this.getGamePath(), 'result', result);
            // Our local cache of rounds probably doesn't have this yet (it could if the listen callback
            // triggered already).
            const roundPath = result.roundPath;
            ctx.assertDefined(roundPath, `Expecting ${roundPath} from ${JSON.stringify(result)} to be defined`);
            const r = this.roundsCache[roundPath];
            if (r) {
                ctx.log('Round', roundPath, 'already in cache');
                return r;
            }
            ctx.log('Fetching Round', roundPath, 'for client');
            ctx = ctx.newCtx('RoundFetch');
            const roundDocRef = this.roundsColl.makeDocRef(roundPath);
            return roundDocRef.get().then(docRef => {
                if (!docRef.docExists()) {
                    throw ctx.newError('Fetch', roundPath, 'got', docRef, 'not no round!');
                }
                const state = docRef.data();
                if (!state) {
                    throw ctx.newError('Invalid Persistent.Round -- no state', docRef.id(), docRef);
                }
                // Check if cache got updated somehow while we were waiting on updates...
                const lazyR = this.roundsCache[roundPath];
                if (lazyR)
                    return lazyR;
                const r = new Round_1.Round(ctx, this.getOptions(), roundDocRef, state);
                this.roundsCache[roundPath] = r;
                ctx.log('Cached round', r.getRoundNumber(), r.getRoundPath(ctx));
                return r;
            });
        });
    }
    static serverNewGameRound(ctx, db, fbFuncs, auth, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath', 'roundNumber']);
        // verify the args
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        (0, ParamValidation_1.isNumProp)(ctx, untrusted_args, 'roundNumber', 0, GameOptions_1.GameOptions.MAX_ROUNDS_PER_GAME);
        const roundNumber = untrusted_args.roundNumber;
        return Game.loadExisting(ctx, db, fbFuncs, gamePath).then(game => {
            // -- current player must be a manager on the Game
            game.ensureCurrentUserIsManager(ctx, auth);
            return game.internalNewRound(ctx, roundNumber)
                .then(r => { return r.getRoundPath(ctx); });
        });
    }
    internalNewRound(ctx, roundNumber) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        if (roundNumber < 0) {
            throw ctx.newError('Round number must be non-negative:', roundNumber);
        }
        if (roundNumber === Round_1.Round.NO_ROUND_NUMBER) {
            throw ctx.newError('Invalid round number:', roundNumber);
        }
        return this.withGameDoc(ctx, state => {
            var _a;
            const gPlayers = state.players;
            const playerPaths = Object.keys(gPlayers);
            const playerDrivers = {};
            for (const playerPath of playerPaths) {
                playerDrivers[playerPath] = ((_a = gPlayers[playerPath]) === null || _a === void 0 ? void 0 : _a.driver) || null;
            }
            ctx.assertEqual(playerPaths.length, this.playerListCache.length, 'state:', state, 'cache', this.playerListCache);
            // Check if this round number already exists or is nonsensical
            let existingRound = null;
            for (const r of Object.values(this.roundsCache)) {
                const existingNum = r.getRoundNumber();
                if (existingNum === roundNumber) {
                    existingRound = r;
                }
                if (existingNum > roundNumber) {
                    throw ctx.newError('Creating round with number', roundNumber, 'but already have a existing', existingNum, 'round');
                }
            }
            if (existingRound !== null) {
                ctx.log('Warning: duplicate round number', roundNumber, 'existing at', existingRound.getRoundPath(ctx));
                return Promise.resolve(existingRound);
            }
            const roundDoc = this.roundsColl.newDocRef();
            return Round_1.Round.game__startNew(ctx, this.getOptions(), playerPaths, roundNumber, roundDoc, playerDrivers, (rpath, round) => {
                if (round === null) {
                    // XXX is this an error?  Or a harmless cache conflict?
                    delete this.roundsCache[rpath];
                }
                else {
                    ctx.assertUndefined(this.roundsCache[rpath], 'New round is already seen?', rpath);
                    this.roundsCache[rpath] = round;
                }
            });
        });
    }
    getCurrentRound(ctx) {
        const cacheSz = Object.keys(this.roundsCache).length;
        if (cacheSz === 0) {
            return null;
        }
        // Assumes roundsCache is always up to date.  That seems reasonable.
        return Round_1.Round.latest(ctx, this.roundsCache);
    }
    forEachRound(_ctx, cb) {
        Object.values(this.roundsCache).forEach(cb);
    }
    getPlayerListCache(_ctx) {
        return this.playerListCache;
    }
    /** Walk through all players involved in this game (active or not). */
    forEachPlayer(ctx, callback) {
        return Promise.all(this.playerListCache.map(p => callback(p)));
    }
    static serverLayoutRoundGuesses(ctx, db, fbFuncs, auth, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath', 'roundPath']);
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        (0, ParamValidation_1.isID)(ctx, untrusted_args.roundPath);
        const roundPath = untrusted_args.roundPath;
        return Game.loadExisting(ctx, db, fbFuncs, gamePath).then(game => {
            game.ensureCurrentUserIsManager(ctx, auth);
            const round = game.roundsCache[roundPath];
            if (!round) {
                throw ctx.newError(`No round "${roundPath}"} on game "${gamePath}`);
            }
            return round.internalLayoutGuesses(ctx)
                .then(() => {
                return true;
            });
        });
    }
    /**
     * Update Game-wide bank with final results from given Round.
     * Make mark the Game as complete too.
     */
    closeRound(ctx, r) {
        const gamePath = this.getGamePath();
        const roundPath = r.getRoundPath(ctx);
        // Security rules are insane if clients can write to Game, so use a Cloud Function
        // to have the manager close the round
        const serverCloseRound = this.fbFuncs.findCallable('closeGameRound');
        const closeRoundArgs = {
            gamePath,
            roundPath,
        };
        return serverCloseRound(closeRoundArgs)
            .catch(err => {
            throw ctx.chainError(err, `closing round ${roundPath}`);
        }).then(_result => {
            ctx.log(`Closed round ${roundPath}`);
        });
    }
    static serverCloseGameRound(ctx, db, fbFuncs, auth, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath', 'roundPath']);
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        (0, ParamValidation_1.isID)(ctx, untrusted_args.roundPath);
        const roundPath = untrusted_args.roundPath;
        return Game.loadExisting(ctx, db, fbFuncs, gamePath).then(game => {
            game.ensureCurrentUserIsManager(ctx, auth);
            const round = game.roundsCache[roundPath];
            if (!round) {
                throw ctx.newError(`No round "${roundPath}"} on game "${gamePath}`);
            }
            return game.internalCloseRound(ctx, round)
                .then(() => {
                return true;
            });
        });
    }
    /**
     * Update Game-wide bank with final results from given Round.
     * Make mark the Game as complete too.
     */
    internalCloseRound(ctx, r) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        const roundPath = r.getRoundPath(ctx);
        const gameDocUpdate = {
            players: {},
            // Adding round to completedRounds prevents (via security rules)
            // duplicate submissions of the results.  A little heavy-handed
            // and also incomplete, but should prevent double-click submit issues.
            completedRounds: {
                [roundPath]: true,
            }
        };
        if (!r.isFinished(ctx)) {
            ctx.log('Warning: closing round', roundPath, 'before it is finished!');
        }
        let anyPlayerUpdate = false;
        // Note Round may have been among a subset of the Game's players, so not all may have winnings
        return this.forEachPlayer(ctx, playerPath => {
            // Get player's winnings for the round being closed, and merge them into
            // the game-level player stats
            return r.getPlayerRoundWinnings(ctx, playerPath).then(winnings => {
                if (!winnings) {
                    return;
                }
                ctx.assertEqual(winnings.net, winnings.betWinnings + winnings.winningGuessPayout - winnings.totalBetcost + winnings.grant);
                const playerUpdate = {};
                let usefulUpdate = false;
                ctx.log('Player', playerPath, `round folds in $${winnings.net}`);
                if (winnings.net !== 0) {
                    playerUpdate.winnings = bitiotic_1.FirebaseV9.FieldPlaceholder.increment(winnings.net);
                    usefulUpdate = true;
                }
                if (winnings.winningGuess !== undefined) {
                    playerUpdate.bestGuessCount = bitiotic_1.FirebaseV9.FieldPlaceholder.increment(1);
                    usefulUpdate = true;
                    if (winnings.winningGuessDelta === 0) {
                        playerUpdate.exactGuessCount = bitiotic_1.FirebaseV9.FieldPlaceholder.increment(1);
                        usefulUpdate = true;
                    }
                }
                let winningBetCount = 0;
                for (const betResult of Object.values(winnings.betPayouts)) {
                    if (betResult.payout > 0) {
                        winningBetCount += 1;
                    }
                }
                if (winningBetCount > 0) {
                    playerUpdate.winningBetCount = bitiotic_1.FirebaseV9.FieldPlaceholder.increment(winningBetCount);
                    usefulUpdate = true;
                }
                if (usefulUpdate) {
                    if (!gameDocUpdate.players) {
                        gameDocUpdate.players = {};
                    }
                    gameDocUpdate.players[playerPath] = playerUpdate;
                    anyPlayerUpdate = true;
                }
            });
        }).then(() => {
            if (!anyPlayerUpdate) {
                return Promise.resolve();
            }
            const patchProm = this.gameDoc.patchIn(ctx.newCtx('update'), gameDocUpdate)
                .then(() => { });
            return this.enqueueWrite(patchProm);
        }).then(() => {
            ctx = ctx.newCtx('endgame');
            const maxRounds = this.getOptions().roundsPerGame(ctx);
            return this.gameDoc.get().then(game => {
                var _a;
                const completedRounds = ((_a = game.data()) === null || _a === void 0 ? void 0 : _a.completedRounds) || {};
                const roundCount = Object.keys(completedRounds).length;
                ctx.log('End game?', roundCount, 'completed, of', maxRounds);
                if (roundCount >= maxRounds) {
                    return this.internalSetGameOver(ctx)
                        .then(() => { });
                }
                return Promise.resolve();
            });
        });
    }
    setGameOver(ctx) {
        const gamePath = this.getGamePath();
        // Security rules are insane if clients can write to Game, so use a Cloud Function
        // to have the manager close the round
        const serverSetGameOver = this.fbFuncs.findCallable('setGameOver');
        const args = {
            gamePath,
        };
        return serverSetGameOver(args).then(result => {
            ctx.log(`Set game over ${gamePath} result`, result);
            if (result.status === 'ok') {
                return;
            }
            throw ctx.newError('setGameOver failed:', result);
        });
    }
    static serverSetGameOver(ctx, db, fbFuncs, auth, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath']);
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        return Game.loadExisting(ctx, db, fbFuncs, gamePath).then(game => {
            // -- current player must be a manager on the Game
            game.ensureCurrentUserIsManager(ctx, auth);
            return game.internalSetGameOver(ctx).then(() => {
                return true;
            });
        }).catch(err => {
            throw ctx.chainError(err, 'setGameOver');
        });
    }
    internalSetGameOver(ctx) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        if (this.completedCache === true) {
            ctx.log('Game already over.');
            return Promise.resolve();
        }
        ctx.log('Game over');
        const completionPatch = {
            completed: true,
        };
        this.completedCache = true;
        return this.enqueueWrite(this.gameDoc.patchIn(ctx, completionPatch));
    }
    static validateGuesses(ctx, obj) {
        if (typeof obj !== 'object') {
            throw ctx.newError('Guesses object not an object');
        }
        if (obj === null) {
            throw ctx.newError('Guesses object is null');
        }
        if (Array.isArray(obj)) {
            throw ctx.newError('obj is an Array');
        }
        const rec = obj;
        const keys = Object.keys(rec);
        for (const k of keys) {
            (0, ParamValidation_1.isID)(ctx, k);
            const guess = rec[k];
            (0, ParamValidation_1.isObjectWithProperties)(ctx, guess, ['guessValue', 'playerPath', 'rawGuess']);
            (0, ParamValidation_1.isNumProp)(ctx, guess, 'guessValue', 0, Number.MAX_SAFE_INTEGER);
            (0, ParamValidation_1.isStrProp)(ctx, guess, 'playerPath', 128);
            (0, ParamValidation_1.isStrProp)(ctx, guess, 'rawGuess', 64);
        }
        return obj;
    }
    static serverSetRoundWinners(ctx, db, fbFuncs, auth, untrusted_args) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        (0, ParamValidation_1.isObjectWithProperties)(ctx, untrusted_args, ['gamePath', 'roundPath',
            'winningSpotPath', 'answer', 'guesses',]);
        (0, ParamValidation_1.isID)(ctx, untrusted_args.gamePath);
        const gamePath = untrusted_args.gamePath;
        (0, ParamValidation_1.isID)(ctx, untrusted_args.roundPath);
        const roundPath = untrusted_args.roundPath;
        (0, ParamValidation_1.isID)(ctx, untrusted_args.winningSpotPath);
        const winningSpotPath = untrusted_args.winningSpotPath;
        (0, ParamValidation_1.isNumProp)(ctx, untrusted_args, 'answer', 0, Number.MAX_SAFE_INTEGER);
        const answer = untrusted_args.answer;
        const guesses = (untrusted_args.guesses === null) ? null : Game.validateGuesses(ctx, untrusted_args.guesses);
        return Game.loadExisting(ctx, db, fbFuncs, gamePath).then(game => {
            game.ensureCurrentUserIsManager(ctx, auth);
            const round = game.roundsCache[roundPath];
            if (!round) {
                throw ctx.newError(`No round "${roundPath}"} on game "${gamePath}`);
            }
            const budgets = game.getPlayerBettingBudgets(ctx);
            return round.internalSetRoundWinners(ctx, answer, winningSpotPath, guesses, budgets)
                .then(() => {
                return true;
            });
        });
    }
    isPlayerActive(ctx, playerPath) {
        return this.withGameDoc(ctx, state => {
            const plyr = state.players[playerPath];
            if (!plyr) { // XXX should not be possible but whatever
                return Promise.resolve(false);
            }
            return Promise.resolve(plyr.isActive);
        });
    }
    isManager(ctx, playerPath) {
        return this.managerListCache.includes(playerPath);
    }
    // Return given player's current winnings (only includes completed rounds)
    getPlayerGameWinnings(ctx, playerPath) {
        return this.withGameDoc(ctx, state => {
            var _a, _b, _c, _d;
            const plyr = state.players[playerPath];
            const winnings = {
                winnings: (_a = plyr === null || plyr === void 0 ? void 0 : plyr.winnings) !== null && _a !== void 0 ? _a : 0,
                bestGuessCount: (_b = plyr === null || plyr === void 0 ? void 0 : plyr.bestGuessCount) !== null && _b !== void 0 ? _b : 0,
                exactGuessCount: (_c = plyr === null || plyr === void 0 ? void 0 : plyr.exactGuessCount) !== null && _c !== void 0 ? _c : 0,
                winningBetCount: (_d = plyr === null || plyr === void 0 ? void 0 : plyr.winningBetCount) !== null && _d !== void 0 ? _d : 0,
            };
            return Promise.resolve(winnings);
        });
    }
    getPlayerBettingBudgets(ctx) {
        return this.withGameDoc(ctx, state => {
            var _a;
            const budgets = {};
            for (const playerPath of this.getPlayerListCache(ctx)) {
                const winnings = (_a = state.players[playerPath]) === null || _a === void 0 ? void 0 : _a.winnings;
                if (winnings) {
                    budgets[playerPath] = winnings;
                }
            }
            return Promise.resolve(budgets);
        });
    }
    // May return an invalid PlayerPath if no player is winning
    getWinningPlayerPath(ctx) {
        let max = -1;
        let winner = null;
        return this.withGameDoc(ctx, state => {
            return this.forEachPlayer(ctx, playerPath => {
                const playerState = state.players[playerPath];
                if (playerState) {
                    const w = playerState.winnings || 0;
                    if (w > max || winner === null) {
                        max = w;
                        winner = playerPath;
                    }
                }
                return Promise.resolve();
            });
        }).then(() => {
            // XXX beware of everyone getting 0s.  Or of ties.
            if (winner === null) {
                const p = Persistent.asPlayerPath('INVALID_PLAYER:NONE_WINNING');
                return Promise.resolve(p);
            }
            return Promise.resolve(winner);
        });
    }
    // Invoke cb for each player (primary or secondary) that shows up in the
    // join request.
    listenForJoinRequest(ctx, cb, errCb) {
        const unsub = bitiotic_1.CancelTracker.track(`Game.${this.gameDoc.id()}.RequestToJoin.accept`, this.requestToJoinColl.onSnapshot(requests => {
            requests.forEach(docRef => {
                var _a;
                const playerPath = Persistent.asPlayerPath(docRef.id());
                const req = docRef.data();
                const accepted = (_a = req === null || req === void 0 ? void 0 : req.accepted) === null || _a === void 0 ? void 0 : _a[playerPath];
                if (accepted !== true) {
                    cb(playerPath, docRef.ref());
                }
                else {
                    // ctx.log('RequestToJoin for', playerPath, 'already accepted:', req);
                }
                if (req && req.secondaries) {
                    Object.keys(req.secondaries).forEach(secondaryPath => {
                        var _a;
                        if (secondaryPath.startsWith(playerPath)) {
                            const secondaryAccepted = (_a = req === null || req === void 0 ? void 0 : req.accepted) === null || _a === void 0 ? void 0 : _a[secondaryPath];
                            if (secondaryAccepted !== true) {
                                cb(Persistent.asPlayerPath(secondaryPath), docRef.ref());
                            }
                            else {
                                // ctx.log('RequestToJoin for secondary',
                                //    secondaryPath, 'already accepted:', req);
                            }
                        }
                        else {
                            ctx.log('Ignoring illegal secondary', secondaryPath, 'must start with', playerPath);
                        }
                    });
                }
            });
        }, error => {
            ctx.log('listenForJoinRequest error (ignored)', error);
            errCb(ctx.chainError(error, 'listenForJoinRequest'));
        }));
        return unsub;
    }
    // Process snapshot of all rounds stored on this Game document. Return most recent round.
    processRoundSnap(ctx, querySnap, playerPath) {
        // Just get all the rounds.  Most will be duplicates (tracked
        // in 'this.roundsCache').
        querySnap.forEach(roundRef => {
            this.loadRoundIfNew(ctx, roundRef, playerPath);
        });
        return this.getCurrentRound(ctx);
    }
    // Fetch all rounds on this Game, and return most recent (if any).
    pullNewRound(ctx, playerPath) {
        return this.roundsColl // TODO: move filtering into getDocs() query?
            .getDocs().then(querySnap => {
            return this.processRoundSnap(ctx, querySnap, playerPath);
        });
    }
    // Listen for updates to the Rounds subcollection, then invoke callback with current round.
    listenForNextRound(ctx, playerPath, cb, errCb) {
        const unsub = bitiotic_1.CancelTracker.track(`Game.${this.gameDoc.id()}.rounds`, this.roundsColl
            // XXX filter out current roundsCache entries
            // .where('players', 'array-contains', playerPath) // XXX flaky?
            // .where('roundNum', 'not in', (current cached round numbers)) // XXX?
            .onSnapshot(querySnap => {
            ctx.log('Query found', querySnap.size(), 'Rounds for game', this.gameDoc.id());
            const round = this.processRoundSnap(ctx, querySnap, playerPath);
            if (round) {
                cb(round);
            }
        }, err => {
            unsub();
            errCb(ctx.chainError(err, 'Round onSnapshot'));
        }));
        return unsub;
    }
    // Implementation for listenForNextRound
    loadRoundIfNew(ctx, roundSnap, playerPath) {
        // Sanity check the Round snapshot ...
        const state = roundSnap.data();
        if (!state) {
            throw ctx.newError('Invalid Persistent.Round -- no state', roundSnap.id(), roundSnap);
        }
        if (!state.players || !state.players.includes(playerPath)) {
            ctx.log('Found round I am not playing:', state.players, 'looking for', playerPath, 'at', roundSnap.id());
            return undefined;
        }
        // First, check if we've already got this round in the cache
        const roundPath = Persistent.asRoundPath(roundSnap.id());
        const cached = this.roundsCache[roundPath];
        if (cached !== undefined) {
            //ctx.log('Already cached round', roundPath, 'number', cached.getRoundNumber());
            ctx.assertTrue(cached.hasActivePlayer(ctx, playerPath));
            return cached;
        }
        // If not, create a new reference and add to the cache
        ctx.log('Definining new Round instance for', roundSnap.id());
        const round = new Round_1.Round(ctx.newCtx('loadRound'), this.getOptions(), roundSnap.ref(), state);
        this.roundsCache[roundPath] = round;
        ctx.log('New round', round.getRoundNumber(), round.getRoundPath(ctx), 'loaded and cached');
        return round;
    }
    // This is a bit fake, as all the gamePlayers are in a list on the gameDoc, so the doc updates
    // frequently, so the player listener will fire excessively.
    listenForGamePlayer(ctx, playerPath, cb, errCb) {
        const unsub = bitiotic_1.CancelTracker.track(`Game.${this.gameDoc.id()}.players`, this.gameDoc.onSnapshot(gameSnap => {
            if (gameSnap && gameSnap.docExists()) {
                const state = gameSnap.data();
                if (state) {
                    this.updateGameDocCache(state);
                    const gp = state.players[playerPath];
                    if (gp) {
                        cb(gp);
                    }
                }
            }
        }, err => {
            errCb(ctx.chainError(err, 'listening on GameDoc for player', playerPath));
        }));
        return unsub;
    }
    listenForNewPlayers(ctx, cb, errCb) {
        const unsub = bitiotic_1.CancelTracker.track(`Game.${this.gameDoc.id()}.newPlayers`, this.gameDoc.onSnapshot(gameSnap => {
            if (gameSnap && gameSnap.docExists()) {
                const state = gameSnap.data();
                if (state) {
                    this.updateGameDocCache(state);
                    cb(this.playerListCache);
                }
            }
        }, err => {
            errCb(ctx.chainError(err, 'listening on GameDoc for new players'));
        }));
        return unsub;
    }
    listenForGameCompleted(ctx, cb, errCb) {
        if (this.completedCache === true) {
            ctx.log('Game is completed (cached)');
            setTimeout(cb, 0);
            return () => { };
        }
        const unsub = bitiotic_1.CancelTracker.track(`Game.${this.gameDoc.id()}.completed`, this.gameDoc.onSnapshot(gameSnap => {
            if (gameSnap && gameSnap.docExists()) {
                const state = gameSnap.data();
                if (state) {
                    this.updateGameDocCache(state);
                    if (this.completedCache) {
                        ctx.log('Game is marked completed.');
                        unsub();
                        cb();
                    }
                }
            }
        }, err => {
            errCb(ctx.chainError(err, 'listening on GameDoc for completed'));
        }));
        return unsub;
    }
}
exports.Game = Game;
//# sourceMappingURL=Game.js.map